import { createLogic } from 'redux-logic'
import { oneGraph } from '@chegg-wt/core/api/oneGraph'
import { AppState, LogicDeps } from '@chegg-wt/core/state'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import { Editor } from '@tiptap/react'
import {
  clearToast,
  proofReaderTransferOfOwnership,
  reviewPaper,
  setActiveFeedbackTab,
  setFeedbackReason,
  setIsTransferOfOwnershipInProgress,
  setPaperId,
  setPaperList,
  setPaperTitle,
  setRequestForNewPaper,
  setWritingAssitantPaperStatus,
  updatePaperHandler,
} from '@chegg-wt/core-wa/state/actions'
import pusherManager from '@chegg-wt/core-wa/utils/pusherManager'
import {
  clearPapersForIndexDb,
  createPaper,
  updatePaper,
} from '@chegg-wt/core-wa/utils/paperIndexedDb'
import fullPaperChecker from '@chegg-wt/core-wa/utils/pusherDataHandlers/fullPaperCheckHandler'
import { CHANNEL_PROOFREAD_FULL_ANALYZE_EVENT } from '@chegg-wt/core-wa/constants/pusher'
import history from '@chegg-wt/core-wa/utils/history/history'
import {
  resetPaperAndEditor,
  generateUniquePaperTitle,
} from '@chegg-wt/core-wa/utils/helpers'
import { PaperStatus } from '@chegg-wt/core-wa/types'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { emitComponentViewEvent } from '@chegg-wt/core/state/instrumentation/actions'
import { componentOrigins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import {
  addUnderlineEditorText,
  createGuestUserPaper,
  mapSentencesToSentenceOffsets,
} from '../utils/helpers'
import { ActionTypes, EMPTY_PAPER } from './constants'
import { setFeedbacks, setSentenceOffsets } from './actions'
import { SentenceOffset } from './types'
import { handlePusherEventData } from './utils'
import {
  feedbacksSelector,
  paperIdSelector,
  paperRequiresRecheckSelector,
  paperStatusSelector,
  paperTitleSelector,
  sentenceOffsetsSelector,
  totalFeedbackCountSelector,
  isTransferOfOwnershipInProgressSelector,
  isNewDocumentRequestedSelector,
  wordCountSelector,
  paperListSelector,
} from './selectors'

/*
Review paper click cases: 
  1. Guest User / SignedIn User: Adds paper and clicks on review button, user has no paperId.
  2. Guest User / SignedIn User: Adds paper and clicks on quick action -> paperId is generated -> may perform some quick action -> then clicks on review (existing paperId)
 */
export const reviewPaperLogic = createLogic<
  AppState,
  ReturnType<typeof reviewPaper>['payload'],
  any,
  LogicDeps
>({
  type: [ActionTypes.RUN_PROOFREAD_CHECKS],
  async process({ action, getState }, dispatch, done) {
    history.clear()
    dispatch(clearToast())
    dispatch(setWritingAssitantPaperStatus(PaperStatus.IN_REVIEW))
    fullPaperChecker.startFullPaperCheck(dispatch, done)
    const editor: Editor = getFeatureEditor()
    const editorText = editor.getText()
    try {
      await pusherManager.subscribe(
        CHANNEL_PROOFREAD_FULL_ANALYZE_EVENT,
        handlePusherEventData
      )
    } catch {
      fullPaperChecker.notifyErrorInPaperCheckCall(dispatch)
      return
    }
    const traceId = await pusherManager.getTraceId()
    const { routes } = action.payload

    const state = getState()
    let paperId = paperIdSelector(state)
    let title = paperTitleSelector(state)
    const paperList = paperListSelector(state)
    const isLoggedIn = loggedInSelector(state)

    const paperContent = {
      editorJSON: editor.getJSON(),
    }

    // We will only add title when creating paper first time
    if (!title && !paperId) {
      title = generateUniquePaperTitle(paperList)
      dispatch(setPaperTitle(title))
    }

    // If paperId is present we will just send paperId so that backend not save the paper
    // eslint-disable-next-line no-nested-ternary
    const paperData = paperId
      ? { paperId }
      : !isLoggedIn
      ? {}
      : { paperContent: JSON.stringify(paperContent), title }

    if (!isLoggedIn) {
      // Guest flow: we need to send paperId either by creating in frontend of if already present
      paperId = await createGuestUserPaper({
        paperId,
        title,
        paperContent,
        status: PaperStatus.IN_REVIEW,
      })
      if (paperId) {
        dispatch(setPaperId(paperId))
        dispatch(navigated(routes.toWritingAssistant({ paperId })))
        // Adding this paperId in graphql call so that we don't save paper for guest user
        paperData.paperId = paperId
      }
    }

    const response = await oneGraph
      .paperCheck({
        request: {
          traceId,
          initiatedAt: new Date().toISOString(),
          paperText: editorText,
          ...paperData,
        },
      })
      .catch(() => {
        fullPaperChecker.notifyErrorInPaperCheckCall(dispatch)
      })

    if (!response) return

    // We get new paper created by backend server
    if (!paperId && response.paperId) {
      paperId = response.paperId
      setPaperId(paperId)

      dispatch(navigated(routes.toWritingAssistant({ paperId })))
    }
    const mappedSentences: SentenceOffset[] = mapSentencesToSentenceOffsets(
      response.sentences,
      editorText
    )
    dispatch(setSentenceOffsets(mappedSentences))
    fullPaperChecker.notifyGetSentences(mappedSentences, dispatch)
  },
})

export const deleteFeedbackLogic = createLogic<
  AppState,
  { feedbackId: string },
  any,
  LogicDeps
>({
  type: [ActionTypes.DELETE_FEEDBACK],
  async process({ getState, action }, dispatch, done) {
    const state = getState()
    const { feedbackId } = action.payload
    const feedbacks = feedbacksSelector(state)
    const filteredFeedbacks = feedbacks.filter((feedback) => feedback.id !== feedbackId)
    dispatch(setFeedbacks(filteredFeedbacks))
    done()
  },
})

export const setPaperRequiresRecheckLogic = createLogic<
  AppState,
  { requiresReCheck: boolean },
  any,
  LogicDeps
>({
  type: [ActionTypes.SET_PAPER_REQUIRES_RECHECK],
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const isRequiresRecheck = paperRequiresRecheckSelector(state)
    if (isRequiresRecheck === action.payload.requiresReCheck) {
      reject(action)
    }
    allow(action)
  },
  async process({ getState, action }, dispatch, done) {
    const state = getState()
    const sentenceOffset = sentenceOffsetsSelector(state)
    const { requiresReCheck } = action.payload
    if (requiresReCheck === true && sentenceOffset.length > 0) {
      dispatch(
        emitComponentViewEvent({
          origin: componentOrigins.proofReaderReEnableReviewButton,
        })
      )
    }
    done()
  },
})

export const updatePaperHandlerLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: [ActionTypes.UPDATE_PAPER_HANDLER],
  async process({ getState }, dispatch, done) {
    const editor: Editor = getFeatureEditor()
    const state = getState()
    const paperId = paperIdSelector(state)
    if (!paperId) return
    const title = paperTitleSelector(state)
    const status = paperStatusSelector(state)
    const feedBackList = feedbacksSelector(state)
    const sentences = sentenceOffsetsSelector(state)
    const requiresRecheck = paperRequiresRecheckSelector(state)
    const totalFeedbackCount = totalFeedbackCountSelector(state)
    const isLoggedIn = loggedInSelector(state)

    const dbSentences = sentences.map((sentence) => ({
      id: sentence.id,
      offset: sentence.offset,
      length: sentence.length,
    }))

    // if (storeProofReaderPapersInLocal()) {
    updatePaper(
      paperId,
      {
        title,
        status,
        paperContent: { editorJSON: editor.getJSON() },
        feedbackState: {
          feedbackList: feedBackList,
          sentences: dbSentences,
          totalFeedbackCount,
        },
        requiresRecheck,
      },
      isLoggedIn
    )
    // }

    done()
  },
})

export const setActiveFeedbackTabLogic = createLogic<
  AppState,
  ReturnType<typeof setActiveFeedbackTab>['payload'],
  any,
  LogicDeps
>({
  type: [ActionTypes.SET_ACTIVE_FEEDBACK_TAB],
  async process({ action, getState }) {
    const state = getState()
    const { activeFeedbackTab } = action.payload
    const feedbacks = feedbacksSelector(state)
    const sentenceOffsets = sentenceOffsetsSelector(state)
    if (activeFeedbackTab >= 0 && feedbacks.length > 0 && sentenceOffsets.length > 0) {
      addUnderlineEditorText(feedbacks, sentenceOffsets, activeFeedbackTab)
    }
  },
})

/*
 Transfer of ownership trigger cases: 
  1. User adds/type paper, with no quick action or review
  2. User adds/type paper, does quick action but no review
  3. User add/type paper, reviews paper
 */

export const setProofReaderTransferOfOwnership = createLogic<
  AppState,
  ReturnType<typeof proofReaderTransferOfOwnership>['payload'],
  any,
  LogicDeps
>({
  type: [ActionTypes.WRITING_ASSISTANT_TRANSFER_OF_OWNERSHIP],
  async process({ action, getState }, dispatch, done) {
    const state = getState()
    const isTransferOfOwnershipInProgress = isTransferOfOwnershipInProgressSelector(state)
    const isNewDocumentRequested = isNewDocumentRequestedSelector(state)

    if (isTransferOfOwnershipInProgress) return
    dispatch(setIsTransferOfOwnershipInProgress(true))

    const paperId = paperIdSelector(state)
    const status = paperStatusSelector(state)
    let title = paperTitleSelector(state)
    const wordCount = wordCountSelector(state)
    const isLoggedIn = loggedInSelector(state)
    const { papers } = await oneGraph.getPaperList({})

    const { routes } = action.payload

    let isPaperSavable = true

    if (wordCount === 0 && !title && !paperId) {
      isPaperSavable = false
    }

    const editor: Editor = getFeatureEditor()
    const paperContent = {
      editorJSON: editor.getJSON(),
    }

    let newPaperId
    if (isPaperSavable) {
      if (!title) {
        title = generateUniquePaperTitle(papers)
        dispatch(setPaperTitle(title))
      }

      newPaperId = await createPaper(
        title,
        paperContent,
        {
          feedbackList: [],
          sentences: [],
          totalFeedbackCount: 0,
        },
        isLoggedIn,
        paperId,
        status
      )
    }

    // ALWAYS FETCHING PAPER LIST: to update paperList component

    if (!isPaperSavable && papers.length === 0) {
      if (!title) {
        title = generateUniquePaperTitle(papers)
        dispatch(setPaperTitle(title))
      }
      newPaperId = await createPaper(
        title,
        paperContent,
        {
          feedbackList: [],
          sentences: [],
          totalFeedbackCount: 0,
        },
        isLoggedIn,
        paperId,
        status
      )
    }
    if (isNewDocumentRequested) {
      // If user has clicked on +icon, Add Action trigger to open new blank page for user.
      dispatch(navigated(routes.toWritingAssistant()))
      resetPaperAndEditor(dispatch)
      const newPapersList = [EMPTY_PAPER, ...papers]
      dispatch(setPaperList(newPapersList))
      dispatch(setRequestForNewPaper(false))
    } else {
      if (newPaperId) {
        dispatch(setPaperId(newPaperId))
        dispatch(navigated(routes.toWritingAssistant({ paperId: newPaperId })))
      } else if (papers[0]?.paperId) {
        // Open latest paper to user
        dispatch(navigated(routes.toWritingAssistant({ paperId: papers[0]?.paperId })))
      }

      dispatch(setPaperList(papers))
    }

    dispatch(setIsTransferOfOwnershipInProgress(false))
    clearPapersForIndexDb()

    done()

    // update paperId in left panel list object(Populated from DB). So that, when user clicks back on paper in left panel we can get data based on paperId ,(getPaper) call.
  },
})

export const setFeedbackReasonLogic = createLogic<
  AppState,
  ReturnType<typeof setFeedbackReason>['payload'],
  any,
  LogicDeps
>({
  type: [ActionTypes.SET_FEEDBACK_REASON],
  process(_, dispatch, done) {
    dispatch(updatePaperHandler())
    done()
  },
})
